import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Bell, Warning, Info, CheckCircle, UserPlus, ShieldCheck, PuzzlePiece, X, ArrowsClockwise
} from '@phosphor-icons/react';

const SEVERITY_CONFIG = {
  high: { color: 'text-red-600', bg: 'bg-red-50 border-red-200', icon: Warning },
  medium: { color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200', icon: Info },
  low: { color: 'text-blue-600', bg: 'bg-blue-50 border-blue-200', icon: Info },
};

const TYPE_ICONS = {
  moderation: Warning,
  registration: UserPlus,
  security: ShieldCheck,
  integration: PuzzlePiece,
  health: CheckCircle,
  system: Info,
};

export default function CmsNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/cms/notifications`, {
        withCredentials: true,
      });
      setNotifications(r.data.items || []);
    } catch (e) {
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const refresh = async () => {
    setRefreshing(true);
    await loadNotifications();
    setRefreshing(false);
  };

  const dismiss = async (notif) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/cms/notifications/${notif.id}/dismiss`,
        {},
        { withCredentials: true }
      );
      setNotifications((prev) => prev.filter((n) => n.id !== notif.id));
      toast.success('Notification dismissed');
    } catch (e) {
      toast.error('Failed to dismiss');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin h-8 w-8 border-b-2 border-primary rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cms-notifications">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Bell size={28} weight="duotone" className="text-primary" />
            Admin Notifications
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            System alerts, moderation queue, integration health, and site activity
          </p>
        </div>
        <button
          onClick={refresh}
          disabled={refreshing}
          className="inline-flex items-center gap-1.5 px-4 py-2 border border-border rounded-lg hover:bg-muted text-sm"
          data-testid="refresh-notifications"
        >
          <ArrowsClockwise size={16} weight={refreshing ? 'bold' : 'regular'} />
          Refresh
        </button>
      </div>

      {/* Notifications List */}
      {notifications.length === 0 ? (
        <div className="bg-card border border-border rounded-lg p-12 text-center">
          <Bell size={48} weight="duotone" className="text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground font-semibold">All caught up!</p>
          <p className="text-sm text-muted-foreground mt-1">No notifications at the moment.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="notifications-list">
          {notifications.map((notif) => {
            const severityConfig = SEVERITY_CONFIG[notif.severity] || SEVERITY_CONFIG.low;
            const TypeIcon = TYPE_ICONS[notif.type] || Info;
            const Icon = severityConfig.icon;

            return (
              <div
                key={notif.id}
                className={`border rounded-lg p-4 ${severityConfig.bg}`}
                data-testid={`notification-${notif.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="flex items-center gap-2">
                    <TypeIcon size={20} weight="duotone" className={severityConfig.color} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm">{notif.title}</p>
                    <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                      <span className="uppercase tracking-widest">{notif.type}</span>
                      <span>·</span>
                      <span className="capitalize">{notif.severity} priority</span>
                      <span>·</span>
                      <span>{new Date(notif.created_at).toLocaleString()}</span>
                    </div>
                    {notif.link && (
                      <a
                        href={notif.link}
                        className="text-xs text-primary hover:underline mt-1 inline-block"
                      >
                        View details →
                      </a>
                    )}
                  </div>
                  <button
                    onClick={() => dismiss(notif)}
                    className="text-muted-foreground hover:text-foreground p-1"
                    title="Dismiss"
                    data-testid={`dismiss-${notif.id}`}
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Summary Stats */}
      {notifications.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-4">
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Summary</p>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-red-600">
                {notifications.filter((n) => n.severity === 'high').length}
              </p>
              <p className="text-xs text-muted-foreground uppercase tracking-widest">High</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-600">
                {notifications.filter((n) => n.severity === 'medium').length}
              </p>
              <p className="text-xs text-muted-foreground uppercase tracking-widest">Medium</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-600">
                {notifications.filter((n) => n.severity === 'low').length}
              </p>
              <p className="text-xs text-muted-foreground uppercase tracking-widest">Low</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
